'use client'

import React from 'react'
import { Broker } from './types'
import styles from './ConnectorsPanel.module.css'
import { useAccountInfo, useDisconnectBroker } from './hooks/useConnectorQueries'

interface AccountInfoProps {
  broker: Broker
  onDisconnect: () => void
}

const AccountInfo: React.FC<AccountInfoProps> = ({
  broker,
  onDisconnect
}) => {
  // Use React Query hook instead of useEffect
  const { 
    data: accountInfo, 
    isLoading, 
    error,
    refetch 
  } = useAccountInfo(broker.id, broker.isConnected)
  
  const disconnectMutation = useDisconnectBroker()

  const handleDisconnect = async () => {
    disconnectMutation.mutate(broker.id, {
      onSuccess: () => {
        onDisconnect()
      },
      onError: (error) => {
        console.error('Failed to disconnect:', error)
        alert(`Failed to disconnect from ${broker.name}`)
      }
    })
    }

  const handleRefresh = () => {
    refetch()
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.accountInfo}>
        <h3>Loading account information...</h3>
        <div className="flex items-center justify-center p-4">
          <div className="text-gray-500">Fetching account details...</div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={styles.accountInfo}>
        <h3>{broker.name} Account</h3>
        <div className="text-red-500 p-4">
          <div className="mb-2">Failed to load account information:</div>
          <div className="text-sm">{error.message}</div>
          <button 
            onClick={handleRefresh}
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
        <button 
          className={styles.disconnectButton} 
          onClick={handleDisconnect}
          disabled={disconnectMutation.isPending}
        >
          {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
        </button>
      </div>
    )
  }

  return (
    <div className={styles.accountInfo}>
      <div className="flex items-center justify-between mb-4">
      <h3>{broker.name} Account</h3>
        <button 
          onClick={handleRefresh}
          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
          title="Refresh account info"
        >
          🔄 Refresh
        </button>
      </div>

      {accountInfo ? (
        <div className={styles.accountDetails}>
          <div className={styles.accountCard}>
            <h4>Account Details</h4>
            <div className={styles.accountProperty}>
              <span className={styles.propertyLabel}>VIP Level:</span>
              <span className={styles.propertyValue}>
                {accountInfo.vipLevel}
              </span>
            </div>
            <div className={styles.accountProperty}>
              <span className={styles.propertyLabel}>Margin Enabled:</span>
              <span className={styles.propertyValue}>
                {accountInfo.isMarginEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={styles.accountProperty}>
              <span className={styles.propertyLabel}>Future Enabled:</span>
              <span className={styles.propertyValue}>
                {accountInfo.isFutureEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={styles.accountProperty}>
              <span className={styles.propertyLabel}>Options Enabled:</span>
              <span className={styles.propertyValue}>
                {accountInfo.isOptionsEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={styles.accountProperty}>
              <span className={styles.propertyLabel}>
                Portfolio Margin Enabled:
              </span>
              <span className={styles.propertyValue}>
                {accountInfo.isPortfolioMarginRetailEnabled ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <p>No account information available</p>
      )}

      <button 
        className={styles.disconnectButton} 
        onClick={handleDisconnect}
        disabled={disconnectMutation.isPending}
      >
        {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
      </button>
      
      {disconnectMutation.error && (
        <div className="text-red-500 text-sm mt-2">
          Disconnect failed: {disconnectMutation.error.message}
        </div>
      )}
    </div>
  )
}

export default AccountInfo
